import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../AuthComponent';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon, ChevronDownIcon, AlertTriangleIcon, CalendarIcon, DeleteIcon, RefreshIcon,
  LockIcon, PersonIcon, EmailIcon, ExternalIcon, FolderIcon,
} from '@shopify/polaris-icons';
import { api } from './apiClient';
import { useAlerts } from '../alerts';
import { Ic, StatusPill, EmDash } from '../ui';
import SystemsTable from './SystemsTable';
import ForwardingDialog from './ForwardingDialog';
import {
  accountState, runState, pillTone, systemLabel, formatDateTime, formatTime, isScheduled,
  retryBlockedReason, splitRecipients, buildActivity,
} from './status';

// Archive progress is only computed on a full refresh (it costs a GAM
// call), so keep the last known value when an action returns a new tree.
function withKnownProgress(prev, next) {
  if (next.archive && !next.archive.progress && prev?.archive?.progress) {
    return { ...next, archive: { ...next.archive, progress: prev.archive.progress } };
  }
  return next;
}

function ArchiveCard({ info, archive }) {
  const done = !!info.folder_url;
  const failed = archive?.status === 'failure';
  const skipped = archive?.outcome === 'skipped';
  const { progress } = info;
  const pending = failed ? 'not uploaded' : 'after restore';
  return (
    <div className="uh-card">
      <div className="uh-card-h">
        <h2 className="uh-h2">Archive</h2>
        <span className="uh-sp" />
        <a
          className="uh-btn slim"
          href={info.folder_url || undefined}
          target="_blank"
          rel="noopener noreferrer"
          aria-disabled={!done}
          title={done ? undefined : 'Available when the archive finishes'}
        >
          <Ic source={ExternalIcon} sm />Open folder
        </a>
      </div>
      <div className="uh-card-b" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {info.folder_name && <div className="uh-help">Terminated User Backups / {info.folder_name}</div>}
        {!info.backup_started ? (
          <span className="uh-help">
            {skipped ? 'Not archived by this tool. Phase 1 was finished by hand, so the mail backup never ran.'
              : 'Nothing archived yet. Phase 1 hasn’t started the mail backup.'}
          </span>
        ) : (
          <>
            {progress && !done && (
              <div className="uh-arc-prog">
                <span>
                  {progress.phase === 'backup' ? 'Backing up mail' : 'Restoring mail to offboarded-user-archive@'} ·{' '}
                  <span className="uh-num">{progress.current.toLocaleString()} of {progress.total.toLocaleString()}</span> messages
                </span>
                <span className="bar"><i style={{ width: `${Math.min(100, Math.round((progress.current / (progress.total || 1)) * 100))}%` }} /></span>
              </div>
            )}
            {failed && <span className="uh-help uh-err">The archive failed. Retry it from the Google Workspace row.</span>}
            <div className="uh-files">
              <div className="uh-file">
                <Ic source={EmailIcon} sm /><span className="uh-mono">{info.mbox_name}</span>
                <span className="sz">{done && info.mbox_messages != null ? `${info.mbox_messages.toLocaleString()} messages` : pending}</span>
              </div>
              <div className="uh-file">
                <Ic source={FolderIcon} sm /><span className="uh-mono">{info.zip_name}</span>
                <span className="sz">{done && info.drive_files != null ? `${info.drive_files.toLocaleString()} files` : pending}</span>
              </div>
            </div>
            {done && info.finished_on && <div className="uh-help">Finished {info.finished_on}</div>}
          </>
        )}
      </div>
    </div>
  );
}

function ActivityCard({ run }) {
  const { nameOf } = useAuth();
  const items = buildActivity(run, nameOf);
  return (
    <div className="uh-card">
      <div className="uh-card-h"><h2 className="uh-h2">Activity</h2></div>
      <div className="uh-card-b">
        <ul className="uh-tl">
          {items.map((item) => (
            <li key={item.i} className={item.tone}>
              <span className="pt" />
              <div>{item.text}<div className="w">{formatTime(item.at)}</div></div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

const ARCHIVE_ACTION_KEY = 'archive_mail_and_drive';
const POLL_MS = 5000;

// CSV-only departures have no /full-run/{id} row; their tree only exists
// inside the History list, so find it there by the email in the id.
async function loadCsvPseudoRun(id) {
  const email = id.slice('csv-'.length);
  const data = await api.fullRunHistory(email, false);
  const run = (data.runs || []).find((r) => String(r.id) === id);
  if (!run) throw new Error(`No offboarding record found for ${email}.`);
  return run;
}

function Segs({ tones }) {
  return <div className="uh-segs">{tones.map((t, i) => <i key={i} className={t} />)}</div>;
}

function RunDetail() {
  const { nameOf } = useAuth();
  const { runId } = useParams();
  const navigate = useNavigate();
  const { refresh: refreshAlerts } = useAlerts();
  const id = decodeURIComponent(runId);
  const numericId = /^\d+$/.test(id) ? Number(id) : null;

  const [run, setRun] = useState(null);
  const [error, setError] = useState('');
  const [busyActionId, setBusyActionId] = useState(null);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [fwdOpen, setFwdOpen] = useState(false);
  const [toolBusy, setToolBusy] = useState(false);
  const menuRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = numericId !== null ? await api.getFullRun(numericId) : await loadCsvPseudoRun(id);
      setRun(data);
      setError('');
    } catch (e) {
      setError(e.message || String(e));
    }
  }, [id, numericId]);

  useEffect(() => { load(); }, [load]);

  // Keep polling while anything is still running — Workspace's archive
  // step can take hours for a large mailbox.
  useEffect(() => {
    if (!run || numericId === null) return undefined;
    if (!run.accounts.some((a) => a.status === 'in_progress')) return undefined;
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [run, numericId, load]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (e) => { if (!menuRef.current?.contains(e.target)) setMenuOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [menuOpen]);

  const applyTree = (next) => setRun((prev) => withKnownProgress(prev, next));

  const onAction = async (actionId, request) => {
    setBusyActionId(actionId);
    try {
      applyTree(await request());
      setError('');
      refreshAlerts();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusyActionId(null);
    }
  };

  const resolve = (actionId, decision, candidateId) =>
    onAction(actionId, () => api.resolveFullRunAction(numericId, actionId, decision, candidateId));
  const retryStep = (actionId) => onAction(actionId, () => api.retryFullRunAction(numericId, actionId));
  const markDone = (actionId) => onAction(actionId, () => api.markFullRunActionDone(numericId, actionId));

  const retryAll = async () => {
    setMenuOpen(false);
    setToolBusy(true);
    try {
      const data = await api.retryFailedFullRun(numericId);
      applyTree(data);
      setError((data.retry_problems || []).join(' '));
      refreshAlerts();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setToolBusy(false);
    }
  };

  // Errors surface inside the dialog, so let them propagate.
  const saveForwarding = async (recipients) => {
    applyTree(await api.changeFullRunForwarding(numericId, recipients));
    setFwdOpen(false);
  };

  const cancelScheduled = async () => {
    setMenuOpen(false);
    setCancelBusy(true);
    try {
      setRun(await api.cancelFullRun(numericId));
      refreshAlerts();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setCancelBusy(false);
    }
  };

  const back = (
    <div className="uh-crumb">
      <button type="button" onClick={() => navigate('/offboarding?tab=history')}>
        <Ic source={ArrowLeftIcon} sm />Offboarding
      </button>
      <span>/</span><span>History</span>
    </div>
  );

  if (!run) {
    return (
      <div className="uh uh-page">
        {back}
        {error
          ? <div className="uh-alert crit"><Ic source={AlertTriangleIcon} sm /><div>{error}</div></div>
          : <div className="uh-card uh-empty">Loading…</div>}
      </div>
    );
  }

  const [tone, label] = runState(run);
  const accounts = run.accounts || [];
  const states = accounts.map((a) => accountState(a)[0]);
  const settled = states.filter((t) => t === 'ful' || t === 'none' || t === 'skip').length;
  const waiting = accounts.filter((a) => a.status === 'requires_feedback');
  const failed = accounts.filter((a) => a.status === 'failure');
  const scheduled = isScheduled(run);
  const archive = accounts.find((a) => a.system === 'workspace')?.actions.find((a) => a.action_key === ARCHIVE_ACTION_KEY);
  const archiveState = archive ? accountState({ status: archive.status, actions: [archive] }) : null;
  const readOnly = numericId === null;
  const forwardTo = splitRecipients(run.forward_to);
  const canEditForwarding = !readOnly && !!run.email && !!run.archived_email
    && run.archived_email.toLowerCase() !== run.email.toLowerCase();
  const failedSteps = accounts.flatMap((acc) => acc.actions.filter((a) => a.status === 'failure').map((a) => ({ acc, a })));
  const retryable = failedSteps.filter(({ a }) => !retryBlockedReason(a));
  const retryNames = [...new Set(retryable.map(({ acc }) => systemLabel(acc.system)))];
  let retryHint = `Runs ${retryNames.join(', ')} again. Nothing else is touched.`;
  if (!failedSteps.length) retryHint = 'Nothing has failed';
  else if (!retryable.length) retryHint = 'Workspace can’t be retried after a rename. Mark it done instead.';

  return (
    <div className="uh uh-page">
      {back}
      <div className="uh-ph">
        <div>
          <div className="uh-title-row">
            <h1 className="uh-h1">{run.full_name || run.email}</h1>
            <StatusPill tone={pillTone(tone)}>{label}</StatusPill>
          </div>
          <div className="uh-meta">
            {run.email ? <span className="uh-mono">{run.email}</span> : <span>No company email</span>}
            <span className="dot" />
            <span>{run.initiated_by ? `Started by ${nameOf(run.initiated_by)}` : 'Via CSV import'}</span>
            {run.created_at && (<><span className="dot" /><span className="uh-num">{formatDateTime(run.created_at)}</span></>)}
          </div>
        </div>
        <div className="uh-actions">
          <button type="button" className="uh-btn" onClick={load}><Ic source={RefreshIcon} sm />Refresh</button>
          <div className="uh-menu-wrap" ref={menuRef}>
            <button type="button" className="uh-btn" aria-expanded={menuOpen} aria-haspopup="true" onClick={() => setMenuOpen((v) => !v)}>
              Tools<Ic source={ChevronDownIcon} sm />
            </button>
            {menuOpen && (
              <div className="uh-menu" role="menu">
                <button type="button" role="menuitem" disabled={readOnly || !retryable.length || toolBusy} onClick={retryAll}>
                  <Ic source={RefreshIcon} />
                  <span>
                    <span className="ml">{toolBusy ? 'Retrying…' : 'Retry failed steps'}</span>
                    <span className="ms">{retryHint}</span>
                  </span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  disabled={!canEditForwarding}
                  onClick={() => { setMenuOpen(false); setFwdOpen(true); }}
                >
                  <Ic source={EmailIcon} />
                  <span>
                    <span className="ml">Change mail forwarding</span>
                    <span className="ms">
                      {canEditForwarding
                        ? `Edit who keeps receiving ${run.email.split('@')[0]}@ mail`
                        : 'This run was suspend-only, so there’s no forwarding to edit'}
                    </span>
                  </span>
                </button>
                <button type="button" role="menuitem" disabled={!scheduled || readOnly || cancelBusy} onClick={cancelScheduled}>
                  <Ic source={CalendarIcon} />
                  <span>
                    <span className="ml">Cancel scheduled run</span>
                    <span className="ms">{scheduled ? `Stops everything planned for ${formatDateTime(run.scheduled_for)}` : 'Only for runs still waiting on their scheduled time'}</span>
                  </span>
                </button>
                <hr />
                <button type="button" role="menuitem" className="danger" onClick={() => navigate('/offboarding?tab=delete')}>
                  <Ic source={DeleteIcon} />
                  <span>
                    <span className="ml">Delete account…</span>
                    <span className="ms">Workspace accounts appear on the Delete tab once their archive finishes</span>
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && <div className="uh-alert crit"><Ic source={AlertTriangleIcon} sm /><div>{error}</div></div>}

      {scheduled && (
        <div className="uh-alert info">
          <Ic source={CalendarIcon} sm />
          <div>
            <b>Scheduled for {formatDateTime(run.scheduled_for)}.</b> Every match below has already been searched.
            Anything that needs attention must be approved before then; nothing else happens until that time.
          </div>
          <button type="button" className="uh-btn slim" disabled={cancelBusy} onClick={cancelScheduled}>
            {cancelBusy ? 'Cancelling…' : 'Cancel scheduled run'}
          </button>
        </div>
      )}

      {waiting.length > 0 && (
        <div className="uh-alert">
          <Ic source={AlertTriangleIcon} sm />
          <div>
            <b>{waiting.map((a) => systemLabel(a.system)).join(', ')} {waiting.length === 1 ? 'needs' : 'need'} your decision.</b>{' '}
            Open {waiting.length === 1 ? 'the row' : 'each row'} below to approve a match or mark a manual step done.
          </div>
        </div>
      )}
      {failed.length > 0 && (
        <div className="uh-alert crit">
          <Ic source={AlertTriangleIcon} sm />
          <div><b>{failed.map((a) => systemLabel(a.system)).join(', ')} failed.</b> The error is shown in the row below.</div>
        </div>
      )}

      <div className="uh-stages">
        <div className="uh-stage">
          <div className="top"><Ic source={LockIcon} sm />Systems finished<span className="v">{settled}<small>/{accounts.length}</small></span></div>
          <Segs tones={states.map((t) => (t === 'none' || t === 'skip' ? 'ful' : t))} />
          <div className="uh-stage-sub">Includes systems where no account was found</div>
        </div>
        <div className="uh-stage">
          <div className="top"><Ic source={PersonIcon} sm />Needs you<span className="v">{waiting.length + failed.length}</span></div>
          <Segs tones={[...waiting.map(() => 'hold'), ...failed.map(() => 'can')].concat(waiting.length + failed.length ? [] : [''])} />
          <div className="uh-stage-sub">
            {waiting.length + failed.length ? `${waiting.length} waiting · ${failed.length} failed` : 'Nothing waiting on a person'}
          </div>
        </div>
        <div className="uh-stage">
          <div className="top">
            <Ic source={EmailIcon} sm />Mail &amp; Drive archive
            <span className="v">{archiveState ? archiveState[1] : <EmDash />}</span>
          </div>
          <Segs tones={[archiveState ? archiveState[0] : '']} />
          <div className="uh-stage-sub" title={archive?.detail || ''}>
            {archive ? (archive.detail || 'Terminated User Backups') : 'No Workspace account in this run'}
          </div>
        </div>
      </div>

      <div className="uh-od-grid">
        <div className="uh-col">
          <SystemsTable
            run={run}
            onResolve={resolve}
            onRetry={retryStep}
            onMarkDone={markDone}
            busyActionId={busyActionId}
            readOnly={readOnly}
          />
        </div>
        <div className="uh-col">
          <div className="uh-card">
            <div className="uh-card-h"><h2 className="uh-h2">Employee</h2></div>
            <div className="uh-card-b">
              <div className="uh-kv">
                <span>Name</span><b>{run.full_name || <EmDash />}</b>
                <span>Email</span><b className="uh-mono">{run.email || <EmDash />}</b>
                <span>Archived as</span><b className="uh-mono">{run.archived_email || <EmDash />}</b>
                <span>Started</span><b className="uh-num">{formatDateTime(run.created_at) || <EmDash />}</b>
                <span>Scheduled for</span><b className="uh-num">{run.scheduled_for ? formatDateTime(run.scheduled_for) : 'Ran immediately'}</b>
              </div>
            </div>
          </div>
          <div className="uh-card">
            <div className="uh-card-h">
              <h2 className="uh-h2">Mail forwarding</h2>
              <span className="uh-sp" />
              {canEditForwarding && <button type="button" className="uh-btn slim" onClick={() => setFwdOpen(true)}>Change</button>}
            </div>
            <div className="uh-card-b">
              {forwardTo.length ? (
                <div className="uh-kv">
                  {forwardTo.map((addr, i) => (
                    <React.Fragment key={addr}>
                      <span>{i === 0 ? 'Forwards to' : ''}</span><b className="uh-mono">{addr}</b>
                    </React.Fragment>
                  ))}
                  {canEditForwarding && (<><span>Continuity group</span><b className="uh-mono">{run.email}</b></>)}
                </div>
              ) : (
                <span className="uh-help">No forwarding. The account is suspended and archived only.</span>
              )}
            </div>
          </div>
          {run.archive && <ArchiveCard info={run.archive} archive={archive} />}
          <ActivityCard run={run} />
        </div>
      </div>
      {canEditForwarding && (
        <ForwardingDialog
          open={fwdOpen}
          groupEmail={run.email}
          forwardTo={run.forward_to}
          onSave={saveForwarding}
          onClose={() => setFwdOpen(false)}
        />
      )}
    </div>
  );
}

export default RunDetail;
